import { useCallback, useState } from 'react';
import { useReduxForm } from '@rainbow-modules/hooks';
import { Lookup } from 'react-rainbow-components';
import { LookupValue } from 'react-rainbow-components/components/types';
import getLookupOptions from './helpers/getLookupOptions';
import { ExtendedLookupProps, ExtendedLookupValueType } from './types';
import getFormattedValue from './helpers/getFormattedValue';

const EmptyIcon = () => null;

const ExtendedLookup = <T extends unknown = string>(props: ExtendedLookupProps<T>) => {
    const {
        options: optionsProp = [],
        icon = <EmptyIcon />,
        value,
        onSearch = () => {},
        onChange = () => {},
        ...otherLookupProps
    } = useReduxForm(props);
    const [options, setOptions] = useState(optionsProp);

    const handleSearch = useCallback((searchValue: string) => {
        setOptions(getLookupOptions(optionsProp, searchValue));
        return onSearch(searchValue);
    }, [onSearch, optionsProp]);

    const handleChange = useCallback((option?: LookupValue | null) => {
        if (!option) return onChange(null);
        const { isSearchOption, value: optionValue } = option as ExtendedLookupValueType<T>;
        if (isSearchOption) {
            return onChange({
                label: optionValue,
                value: optionValue?.trim(),
            });
        }
        return onChange(option);
    }, [onChange]);

    return (
        <Lookup
            {...otherLookupProps}
            icon={icon}
            options={options}
            value={getFormattedValue<T>(value)}
            onSearch={handleSearch}
            onChange={handleChange}
        />
    );
};

export default ExtendedLookup;
